import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';
import { environment } from '../../../../environments/environment';
import { Season } from './interface/season.interface';
import { rxResource } from '@angular/core/rxjs-interop';
import { concatGraphics } from '@board/utils/board-concat';
import { buildDefaultTimeline } from '@board/utils/board-animation';
import { withAnimationPauses } from '@board/utils/board-timeline';

export type AlertType = 'info' | 'success' | 'error' | 'warning';

export interface ImageViewerAnimation {
  json: any;
  courtMode: string;
}

/** Contenido de un gráfico dentro de una línea de ejercicio. */
export interface GraphicLineContent {
  image: string;
  json?: any;
  courtMode?: string;
  description?: string;
}

/** Línea de ejercicio: la línea 0 es la Original y las siguientes las Alternativas. */
export interface GraphicLine {
  graphics: GraphicLineContent[];
}

/** Imágenes y animaciones de una línea dentro del visor. */
export interface ImageViewerLine {
  images: string[];
  /** Animaciones alineadas con `images` (null si esa imagen no tiene animación). */
  animations: Array<ImageViewerAnimation | null>;
}

export interface ImageViewerData {
  /** Líneas del visor: cada línea tiene su propia lista de imágenes/animaciones. */
  lines: ImageViewerLine[];
  /** Línea activa (0 = Original, 1..N = Alternativa N). */
  lineIndex?: number;
  /** Índice de imagen dentro de la línea activa. */
  index?: number;
  /** Nombre del ejercicio/jugada para nombrar los archivos descargados. */
  name?: string;
}
export interface AlertData {
  type: AlertType;
  message: string;
  duration?: number; // en ms
}

const baseUrl = environment.baseUrl;

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private http = inject(HttpClient);
  private dialog = inject(Dialog);

  private countriesCache = new Map<string, any>();
  private seasonsCache = new Map<string, Season[]>();

  countriesResource = rxResource({ stream: () => this.getCountries() });

  alertSignal = signal<AlertData | null>(null);
  imageViewerSignal = signal<ImageViewerData | null>(null);

  showAlert(type: AlertType, message: string, duration = 3000) {
    this.alertSignal.set({ type, message, duration });
    setTimeout(() => this.alertSignal.set(null), duration);
  }

  showImageViewer(images: string[] | string, index: number = 0, animations: Array<ImageViewerAnimation | null> = [], name?: string) {
    const list = Array.isArray(images) ? images : [images];
    this.imageViewerSignal.set({ lines: [{ images: list, animations }], lineIndex: 0, index, name });
  }

  /**
   * Muestra las líneas de un ejercicio en el visor: cada línea tiene su propia
   * lista de imágenes y animaciones y se elige con chips dentro del visor
   * (Original / Alternativa N). Los gráficos ausentes de una línea se omiten.
   * Acepta líneas en formato `{ graphics: [...] }` o aplanado `[[...]]`.
   */
  showGraphicsViewer(
    lines:
      | Array<{ graphics?: Array<{ image: string; json?: any; courtMode?: string }> }>
      | Array<Array<{ image: string; json?: any; courtMode?: string }>>
      | undefined,
    lineIndex: number = 0,
    index: number | string = 0,
    name?: string,
  ) {
    // Compatibilidad con las llamadas legacy: (graphics, index, name).
    const viewerIndex = typeof index === 'number' ? index : lineIndex;
    const viewerName = typeof index === 'string' ? index : name;
    const sourceLines: Array<Array<{ image: string; json?: any; courtMode?: string }>> = Array.isArray(lines?.[0])
      ? (lines as Array<Array<{ image: string; json?: any; courtMode?: string }>>)
      : ((lines as Array<{ graphics?: Array<{ image: string; json?: any; courtMode?: string }> }> | undefined) ?? []).map((line) => line?.graphics ?? []);
    const viewerLines: ImageViewerLine[] = sourceLines.map((line) => ({
      images: line.map((g) => g.image),
      animations: line.map((g) => (g.json ? { json: g.json, courtMode: g.courtMode || 'full' } : null)),
    }));
    if (viewerLines.length === 0) return;
    const safeLineIndex = Math.min(Math.max(0, lineIndex), viewerLines.length - 1);
    const safeIndex = Math.min(Math.max(0, viewerIndex), Math.max(0, viewerLines[safeLineIndex].images.length - 1));
    this.imageViewerSignal.set({ lines: viewerLines, lineIndex: safeLineIndex, index: safeIndex, name: viewerName });
  }

  closeImageViewer() {
    this.imageViewerSignal.set(null);
  }

  /** Cambia la línea activa del visor (el índice se clampa al rango de la línea). */
  setImageViewerLine(lineIndex: number) {
    const data = this.imageViewerSignal();
    if (!data || !data.lines[lineIndex]) return;
    const clamped = Math.min(data.index ?? 0, Math.max(0, data.lines[lineIndex].images.length - 1));
    this.imageViewerSignal.set({ ...data, lineIndex, index: clamped });
  }

  /**
   * Cambia el índice del visor de imágenes con wrap-around (índices negativos
   * incluidos) dentro de la línea activa. No hace nada si el visor está
   * cerrado o la línea no tiene imágenes.
   */
  goToImageViewer(index: number) {
    const data = this.imageViewerSignal();
    const images = data?.lines[data.lineIndex ?? 0]?.images ?? [];
    if (!data || images.length === 0) return;
    const wrapped = ((index % images.length) + images.length) % images.length;
    this.imageViewerSignal.set({ ...data, index: wrapped });
  }

  /**
   * Indica si una línea de ejercicio tiene animación reproducible (≥1 gráfico
   * con json en esa línea).
   */
  canPlayLineAnimation(lines: Array<Array<{ json?: any }>>, lineIndex: number): boolean {
    const line = lines[lineIndex];
    return !!line && line.some((g) => !!g?.json);
  }

  /**
   * Reproduce la animación completa de una línea de ejercicio: concatena sus
   * gráficos con las pausas estándar (0.4s inicial, unión única de 0.4s entre
   * gráficos y 0.4s final; nada de sumar la pausa final de uno con la inicial
   * del siguiente).
   */
  async playLineAnimation(lines: Array<Array<{ json?: any; courtMode?: string }>>, lineIndex: number, name?: string): Promise<void> {
    const line = lines[lineIndex];
    if (!line || line.length === 0) return;
    // El primer gráfico lleva la pausa inicial; todos llevan la final (la del
    // último es la pausa final de la animación completa).
    const wrapped = line.map((g, i) => this.withPlaybackPauses(g, i === 0)).filter((g) => g !== null);
    const json = concatGraphics(wrapped);
    if (!json) return;
    const { BoardPreviewDialogComponent } = await import('@board/components/board-preview-dialog/board-preview-dialog.component');
    this.dialog.open(BoardPreviewDialogComponent, {
      data: { json, courtMode: json.courtMode || 'full', title: 'Animación de todos los gráficos', name },
    });
  }

  /**
   * Reproduce la animación de un único gráfico de una línea (pausa de 0.4s al
   * inicio y 0.4s al final).
   */
  async playLineGraphicAnimation(lines: Array<Array<{ json?: any; courtMode?: string }>>, lineIndex: number, graphicIndex: number, name?: string): Promise<void> {
    const graphic = lines[lineIndex]?.[graphicIndex];
    if (!graphic?.json) return;
    const wrapped = this.withPlaybackPauses(graphic, true);
    if (!wrapped?.json) return;
    const { BoardPreviewDialogComponent } = await import('@board/components/board-preview-dialog/board-preview-dialog.component');
    this.dialog.open(BoardPreviewDialogComponent, {
      data: {
        json: wrapped.json,
        courtMode: wrapped.courtMode ?? 'full',
        title: name,
        name,
        graphicNumber: graphicIndex + 1,
      },
    });
  }

  /**
   * Envuelve el json de un gráfico con las pausas estándar de reproducción:
   * pausa inicial (si `leading`) y pausa final de 0.4s como eventos 'hold' de
   * la timeline. No muta el json original. Devuelve null si no hay animación.
   */
  private withPlaybackPauses(graphic: { json?: any; courtMode?: string }, leading: boolean): { json?: any; courtMode?: string } | null {
    const json = graphic.json;
    if (!json) return null;
    // Timeline explícita guardada o la generada por defecto desde las flechas.
    const base = json.timeline && json.timeline.events?.length > 0 ? json.timeline : buildDefaultTimeline(json);
    if (!base) return { ...graphic };
    const cloned = structuredClone(json);
    cloned.timeline = withAnimationPauses(base, { leading, trailing: true });
    return { ...graphic, json: cloned };
  }

  getSeasons(): Observable<Season[]> {
    const key = `all`;
    if (this.seasonsCache.has(key)) {
      return of(this.seasonsCache.get(key)!);
    }
    return this.http.get<Season[]>(`${baseUrl}/common/getSeasons`).pipe(tap((resp) => this.seasonsCache.set(key, resp)));
  }

  getCountries(): Observable<any> {
    const key = `all`;
    if (this.countriesCache.has(key)) {
      return of(this.countriesCache.get(key)!);
    }

    return this.http.get<any>(`${baseUrl}/common/getCountries`).pipe(tap((resp) => this.countriesCache.set('all', resp)));
  }

  /**
   * Optimiza una imagen base64 (jpeg/png) para que pese menos de maxSizeMB MB.
   * @param base64 Imagen en base64 (dataURL)
   * @param maxSizeMB Tamaño máximo en MB (por defecto 2)
   * @param maxWidth Ancho máximo en px (por defecto 800)
   */
  async optimizeBase64Image(base64: string, maxSizeMB = 0.1, maxWidth = 800): Promise<string> {
    if (!base64) return base64;
    return new Promise<string>((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(base64);
        ctx.drawImage(img, 0, 0, width, height);
        let quality = 0.9;
        let result = canvas.toDataURL('image/webp', quality);
        // Reducir calidad si excede el tamaño
        while (result.length > maxSizeMB * 1024 * 1024 && quality > 0.3) {
          quality -= 0.1;
          result = canvas.toDataURL('image/webp', quality);
        }
        resolve(result);
      };
      img.onerror = () => resolve(base64);
      img.src = base64;
    });
  }
}

/**
 * Devuelve el primer gráfico de un ejercicio (formato nuevo `lines`): siempre
 * el primer gráfico de la línea 0 (Original). Se usa en listados y tarjetas
 * para mostrar la previsualización.
 */
export function getFirstGraphic(data: { lines?: Array<{ graphics?: GraphicLineContent[] }> }): GraphicLineContent | undefined {
  const line0 = data.lines?.[0]?.graphics ?? [];
  return line0.find((g) => !!g?.image) ?? line0[0];
}

/**
 * Devuelve el número total de gráficos de un ejercicio sumando todas sus
 * líneas (formato nuevo). Útil para el ordenamiento por cantidad de gráficos.
 */
export function countGraphics(data: { lines?: Array<{ graphics?: unknown[] }> }): number {
  return (data.lines ?? []).reduce((acc, line) => acc + (line?.graphics?.length ?? 0), 0);
}
